import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { adminApi } from "../../lib/api";
import { inputCls } from "./shared";

export function BookingsTab() {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState([]);
  const [raw, setRaw] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    adminApi().get("/admin/bookings").then((r) => setItems(r.data)).catch(() => {});
    adminApi().get("/admin/commissions/summary").then((r) => setSummary(r.data.summary || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const parseInput = () => {
    const txt = raw.trim();
    if (!txt) return [];
    // Accept JSON array
    if (txt.startsWith("[")) return JSON.parse(txt);
    // Otherwise CSV — header: booking_ref,amount_pkr,segments,aff_code,airline_code,route,booked_at
    const lines = txt.split("\n").map((l) => l.trim()).filter(Boolean);
    const header = lines[0].split(",").map((h) => h.trim());
    return lines.slice(1).map((line) => {
      const cells = line.split(",").map((c) => c.trim());
      const obj = {};
      header.forEach((h, i) => (obj[h] = cells[i] || ""));
      return obj;
    });
  };

  const reconcile = async () => {
    setBusy(true);
    try {
      const bookings = parseInput();
      if (!bookings.length) { toast.error("No bookings to import"); return; }
      const { data } = await adminApi().post("/admin/bookings/reconcile", { bookings });
      toast.success(`Imported ${data.imported} · matched ${data.matched_to_affiliate} · PKR ${data.total_commission_pkr}`);
      setRaw(""); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Reconcile failed");
    } finally { setBusy(false); }
  };

  const setStatus = async (ref, status) => {
    await adminApi().put(`/admin/bookings/${ref}/payout?status=${status}`);
    toast.success(`${ref} → ${status}`); load();
  };

  return (
    <div>
      <div className="bg-white border border-slate-200 rounded-lg p-5 mb-6" data-testid="admin-bookings-reconcile">
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs uppercase tracking-wide text-slate-500 font-bold">Reconcile bookings (paste CSV or JSON)</p>
          <button onClick={reconcile} disabled={busy || !raw.trim()} data-testid="admin-bookings-reconcile-btn"
            className="bg-[#970C10] hover:bg-[#7A0A0D] disabled:opacity-40 text-white text-sm font-bold px-4 py-2 rounded-lg">
            {busy ? "Reconciling…" : "Match & compute"}
          </button>
        </div>
        <textarea
          value={raw} onChange={(e) => setRaw(e.target.value)}
          placeholder={`CSV example:\nbooking_ref,amount_pkr,segments,aff_code,airline_code,route,booked_at\nAMD-001,60000,2,ali-traveller-5b39c7,EK,KHI-DXB,2026-08-10\n\n— or JSON array of the same fields —`}
          className={`${inputCls} h-40 font-mono text-xs`} data-testid="admin-bookings-input" />
      </div>

      {summary.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg p-5 mb-6" data-testid="admin-bookings-summary">
          <p className="text-xs uppercase tracking-wide text-slate-500 font-bold mb-3">Commission summary per affiliate</p>
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs uppercase text-slate-500"><th className="pb-2">Affiliate</th><th className="pb-2">Bookings</th><th className="pb-2">Earned (PKR)</th><th className="pb-2">Paid (PKR)</th></tr></thead>
            <tbody>{summary.map((s) => (
              <tr key={s.aff_code} className="border-t border-slate-100">
                <td className="py-2 font-mono text-xs">{s.aff_code}</td>
                <td className="py-2">{s.bookings}</td>
                <td className="py-2 font-bold text-[#970C10]">{s.earned.toLocaleString()}</td>
                <td className="py-2 text-slate-500">{s.paid.toLocaleString()}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <p className="px-5 py-3 text-xs uppercase tracking-wide text-slate-500 font-bold border-b border-slate-100">Recent bookings ({items.length})</p>
        <table className="w-full text-sm min-w-[760px]">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr><th className="px-4 py-3">Ref</th><th className="px-4 py-3">Route</th><th className="px-4 py-3">Amount</th><th className="px-4 py-3">Aff</th><th className="px-4 py-3">Commission</th><th className="px-4 py-3">Status</th></tr>
          </thead>
          <tbody>
            {items.map((b) => (
              <tr key={b.booking_ref} className="border-t border-slate-100 hover:bg-slate-50/70 transition-colors duration-150" data-testid={`booking-row-${b.booking_ref}`}>
                <td className="px-4 py-3 font-mono text-xs">{b.booking_ref}</td>
                <td className="px-4 py-3 text-xs">{b.airline_code} · {b.route}</td>
                <td className="px-4 py-3 text-xs">PKR {b.amount_pkr.toLocaleString()}</td>
                <td className="px-4 py-3 font-mono text-xs">{b.aff_code || "—"}</td>
                <td className="px-4 py-3 font-bold text-[#970C10]">{b.commission_pkr ? `${b.commission_pkr.toLocaleString()}` : "—"}</td>
                <td className="px-4 py-3">
                  <select value={b.payout_status} onChange={(e) => setStatus(b.booking_ref, e.target.value)} className="text-xs border border-slate-200 rounded px-2 py-1" data-testid={`booking-status-${b.booking_ref}`}>
                    <option value="earned">earned</option><option value="paid">paid</option><option value="reversed">reversed</option>
                  </select>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">Paste bookings above to reconcile</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
